/**
 * REKON shared domain layer
 * Imported by: src/platform/store/useOperationalStore.js (web)
 *              mobile/src/store/useTechStore.js (native)
 *
 * Pure JS only — no React, no React Native, no CSS.
 */

// ─── Simplified 5-stage pipeline ─────────────────────────────────────────────

export const STAGES = [
  { id: 'intake',    label: 'Intake',    statuses: ['yard'] },
  { id: 'diagnosis', label: 'Diagnosis', statuses: ['triage', 'waiting'] },
  { id: 'repair',    label: 'Repair',    statuses: ['approval', 'parts-auth', 'parts', 'parts-hold', 'wip'] },
  { id: 'verify',    label: 'Verify',    statuses: ['road-test', 'qc'] },
  { id: 'complete',  label: 'Complete',  statuses: ['done', 'completed'] },
];

// Maps any legacy status string to its simplified stage id
export const STATUS_TO_STAGE = {
  yard:         'intake',
  triage:       'diagnosis',
  waiting:      'diagnosis',
  approval:     'repair',
  'parts-auth': 'repair',
  parts:        'repair',
  'parts-hold': 'repair',
  wip:          'repair',
  'road-test':  'verify',
  qc:           'verify',
  done:         'complete',
  completed:    'complete',
};

// ─── Activity stream ──────────────────────────────────────────────────────────

export const ACTIVITY_TYPES = {
  NOTE:            'note',
  FINDING:         'finding',
  REPAIR:          'repair',
  AUTHORIZATION:   'authorization',
  CUSTOMER_UPDATE: 'customer_update',
  STATUS_CHANGE:   'status_change',
  PHOTO:           'photo',
  BLOCKER_SET:     'blocker_set',
  BLOCKER_CLEARED: 'blocker_cleared',
};

// ─── Blocker model ────────────────────────────────────────────────────────────

export const BLOCKER_TYPES = {
  WAITING_PARTS:         'waiting_parts',
  WAITING_AUTHORIZATION: 'waiting_authorization',
  WAITING_CUSTOMER:      'waiting_customer',
  WAITING_TECH:          'waiting_tech',
  WAITING_TOW:           'waiting_tow',
  WAITING_VENDOR:        'waiting_vendor',
  OTHER:                 'other',
};

export const BLOCKER_LABELS = {
  waiting_parts:         'Waiting on Parts',
  waiting_authorization: 'Waiting on Authorization',
  waiting_customer:      'Waiting on Customer',
  waiting_tech:          'Waiting on Technician',
  waiting_tow:           'Waiting on Tow',
  waiting_vendor:        'Waiting on Vendor',
  other:                 'Blocked',
};

// ─── Legacy pipeline stages (kept for backwards compatibility) ────────────────

export const PIPELINE = [
  { id: 'ro-creation', label: 'Intake',    statuses: ['yard'] },
  { id: 'diag',        label: 'Diag',      statuses: ['triage', 'waiting'] },
  { id: 'approval',    label: 'Approval',  statuses: ['approval', 'parts-auth'] },
  { id: 'parts',       label: 'Parts',     statuses: ['parts', 'parts-hold'] },
  { id: 'repair',      label: 'Repair',    statuses: ['wip'] },
  { id: 'verify',      label: 'Verify',    statuses: ['road-test', 'qc'] },
  { id: 'pmt',         label: 'Ready',     statuses: ['done'] },
];

export const CLOSED_STATUSES = ['completed', 'voided'];
export const ACTIVE_STATUSES = PIPELINE.flatMap(stage => stage.statuses);

// ─── Idle thresholds (ms per status) ─────────────────────────────────────────

export const IDLE_MS = {
  yard:          2  * 3_600_000,
  triage:        1  * 3_600_000,
  waiting:       4  * 3_600_000,
  approval:      8  * 3_600_000,
  parts:         24 * 3_600_000,
  'parts-hold':  24 * 3_600_000,
  'parts-auth':  8  * 3_600_000,
  wip:           4  * 3_600_000,
  'road-test':   2  * 3_600_000,
  qc:            2  * 3_600_000,
  done:          8  * 3_600_000,
};

// ─── Stage aging thresholds (hours in current stage → color level) ────────────

export const STAGE_AGE_THRESHOLDS = {
  yard:         { yellow: 1,  orange: 2,  red: 3  },
  triage:       { yellow: 2,  orange: 3,  red: 4  },
  waiting:      { yellow: 4,  orange: 8,  red: 16 },
  approval:     { yellow: 2,  orange: 4,  red: 8  },
  'parts-auth': { yellow: 2,  orange: 4,  red: 8  },
  parts:        { yellow: 12, orange: 24, red: 48 },
  'parts-hold': { yellow: 12, orange: 24, red: 48 },
  wip:          { yellow: 4,  orange: 8,  red: 16 },
  'road-test':  { yellow: 1,  orange: 2,  red: 4  },
  qc:           { yellow: 1,  orange: 2,  red: 3  },
  done:         { yellow: 4,  orange: 12, red: 24 },
};

// ─── Human-readable status labels ────────────────────────────────────────────

export const STAGE_LABELS = {
  yard:          'Intake',
  triage:        'Triage',
  waiting:       'Diag',
  approval:      'Approval',
  'parts-auth':  'Approval',
  parts:         'Parts',
  'parts-hold':  'Parts',
  wip:           'Repair',
  'road-test':   'Verify',
  qc:            'Quality Control',
  done:          'Ready',
  completed:     'Completed',
  voided:        'Voided',
};

export function normalizeTechNumber(value) {
  return value == null ? '' : String(value).trim();
}

export function isClosedStatus(status) {
  return CLOSED_STATUSES.includes(status);
}

export function isActiveJob(job) {
  return !!job && ACTIVE_STATUSES.includes(job.status);
}

export function getStageForStatus(status) {
  return PIPELINE.find(stage => stage.statuses.includes(status)) || null;
}

export function getStageLabel(status) {
  return STAGE_LABELS[status] || getStageForStatus(status)?.label || status;
}

// ─── Valid next-stage transitions ─────────────────────────────────────────────

export const NEXT_STAGES = {
  yard:         ['waiting'],
  triage:       ['waiting', 'approval', 'wip'],  // backward compat — trucks stuck in triage
  waiting:      ['approval', 'parts', 'wip'],
  approval:     ['parts', 'wip'],
  'parts-auth': ['parts', 'wip'],
  parts:        ['wip'],
  'parts-hold': ['wip'],
  wip:          ['road-test', 'done'],
  'road-test':  ['done', 'wip'],
  qc:           ['done', 'wip'],                 // backward compat — trucks stuck in qc
  done:         ['completed'],
};

// ─── Formatting helpers ───────────────────────────────────────────────────────

/** Format a duration in milliseconds to a human string (e.g. "2h 15m", "3d 4h") */
export function fmtDur(ms) {
  if (!ms || ms <= 0) return '—';
  const totalMin = Math.floor(ms / 60_000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h >= 24) return `${Math.floor(h / 24)}d ${h % 24}h`;
  if (h > 0)   return `${h}h ${m}m`;
  return `${m}m`;
}

/** Format a Unix timestamp (seconds) to an elapsed age string */
export function fmtAge(ts, now = Date.now()) {
  if (!ts) return '—';
  return fmtDur(now - ts * 1000);
}

// ─── Business logic ───────────────────────────────────────────────────────────

/** Index of the pipeline stage containing this status (-1 if not found) */
export function getPipelineIndex(status) {
  return PIPELINE.findIndex(s => s.statuses.includes(status));
}

/** Time spent in the current stage, with a color level based on per-stage thresholds */
export function getStageAge(truck, now = Date.now()) {
  const { status, lastStatusChange } = truck;
  if (!lastStatusChange) return { level: 'fresh', stageMs: 0, hrs: 0 };
  const stageMs = now - lastStatusChange * 1000;
  const hrs     = stageMs / 3_600_000;
  const thr     = STAGE_AGE_THRESHOLDS[status];
  if (!thr) return { level: 'fresh', stageMs, hrs };
  let level = 'fresh';
  if      (hrs >= thr.red)    level = 'red';
  else if (hrs >= thr.orange) level = 'orange';
  else if (hrs >= thr.yellow) level = 'yellow';
  return { level, stageMs, hrs };
}

/**
 * Overall time health relative to estimated completion.
 * Returns null if no estimate is set.
 */
export function getTimeHealth(truck, now = Date.now()) {
  const { estimatedTime, startTime, isPaused, timeInWip } = truck;
  if (!estimatedTime || !startTime) return null;
  const elapsedMs = isPaused
    ? (timeInWip || 0) * 3_600_000
    : now - startTime * 1000 + (timeInWip || 0) * 3_600_000;
  const totalMs = estimatedTime * 3_600_000;
  const pct     = elapsedMs / totalMs;
  if (pct >= 1)    return { status: 'red',   pct };
  if (pct >= 0.75) return { status: 'amber', pct };
  return               { status: 'green', pct };
}

export function getEstimatedHours(job) {
  const direct = Number(job?.estimatedTime);
  if (Number.isFinite(direct) && direct > 0) return direct;

  const categoryHours = (job?.repairCategories || [])
    .reduce((sum, category) => sum + (Number(category?.estimatedTime) || 0), 0);
  return categoryHours > 0 ? categoryHours : 8;
}

export function getJobElapsedMs(job, now = Date.now()) {
  if (!job?.startTime) return 0;
  return now - job.startTime * 1000 + (job.timeInWip || 0) * 3_600_000;
}

export function getJobHealth(job, now = Date.now()) {
  if (!job?.startTime) return 'green';
  const elapsedMs = job.isPaused
    ? (job.timeInWip || 0) * 3_600_000
    : getJobElapsedMs(job, now);
  const pct = elapsedMs / (getEstimatedHours(job) * 3_600_000);
  if (pct >= 1) return 'red';
  if (pct >= 0.75) return 'amber';
  return 'green';
}

/** True if a job has been sitting in its current stage past the idle threshold */
export function isIdle(truck, now = Date.now()) {
  const threshold = IDLE_MS[truck.status];
  if (!threshold || !truck.lastStatusChange) return false;
  return now - truck.lastStatusChange * 1000 > threshold;
}

/**
 * SLA risk level: CRITICAL | HIGH | MEDIUM | LOW
 * Considers hard-down status, promised time, time health, stage age, and
 * overall age since intake — in that order, escalating to the worst signal.
 *
 * IMPORTANT: time health and promised-time checks only fire when a truck has
 * an estimatedTime/promisedTime set. Stage age and overall age are the
 * fallback signals that still escalate a job that has no estimate at all —
 * without them, a job left open for weeks with no estimate would read as
 * permanently LOW/green regardless of how stale it actually is.
 */
export function computeSlaRisk(truck, now = Date.now()) {
  if (!truck || isClosedStatus(truck.status)) return null;
  if (truck.isHardDown) return 'CRITICAL';

  if (truck.promisedTime) {
    const remaining = truck.promisedTime - now;
    const totalSpan = truck.promisedTime - (truck.startTime ? truck.startTime * 1000 : now);
    const pctLeft   = totalSpan > 0 ? remaining / totalSpan : 0;
    if (remaining < 0)             return 'CRITICAL';
    if (remaining < 2 * 3_600_000) return 'HIGH';
    if (pctLeft < 0.25)            return 'HIGH';
    if (pctLeft < 0.5)             return 'MEDIUM';
  }

  const health = getTimeHealth(truck, now);
  if (health?.status === 'red')   return 'CRITICAL';
  if (health?.status === 'amber') return 'HIGH';

  // Stage age — escalates even when no estimate/promise was ever set.
  const stageAge = getStageAge(truck, now);
  if (stageAge.level === 'red')    return 'CRITICAL';
  if (stageAge.level === 'orange') return 'HIGH';
  if (stageAge.level === 'yellow') return 'MEDIUM';

  // Overall age since intake — catches ROs sitting for weeks/months with no
  // estimate, no promise, and a current status with no aging thresholds.
  if (truck.startTime) {
    const ageDays = (now - truck.startTime * 1000) / 86_400_000;
    if (ageDays >= 14) return 'CRITICAL';
    if (ageDays >= 7)  return 'HIGH';
    if (ageDays >= 3)  return 'MEDIUM';
  }

  if (isIdle(truck, now)) return 'MEDIUM';

  return 'LOW';
}

/** Numeric rank for sorting by SLA risk, worst first. */
export const SLA_RANK = { CRITICAL: 3, HIGH: 2, MEDIUM: 1, LOW: 0 };

/**
 * Shop health score 0–100.
 * Weighted: overdue jobs (-40%), idle jobs (-30%), hard-down units (-30%).
 */
export function computeShopHealth(jobs, now = Date.now()) {
  if (!jobs?.length) return 100;
  const active = jobs.filter(isActiveJob);
  if (!active.length) return 100;
  const redCount  = active.filter(t => computeSlaRisk(t, now) === 'CRITICAL').length;
  const idleCount = active.filter(t => isIdle(t, now)).length;
  const downCount = active.filter(t => t.isHardDown).length;
  const score = 100
    - (redCount  / active.length) * 40
    - (idleCount / active.length) * 30
    - (downCount / active.length) * 30;
  return Math.max(0, Math.round(score));
}

export function getActiveJobs(jobs) {
  return (jobs || []).filter(isActiveJob);
}

export function groupJobsByStatus(jobs) {
  const state = {};
  ACTIVE_STATUSES.forEach(status => { state[status] = []; });
  getActiveJobs(jobs).forEach(job => {
    if (state[job.status]) state[job.status].push(job);
  });
  return state;
}

export function groupJobsByPipelineStage(jobs, { sortByPosition = true } = {}) {
  const active = getActiveJobs(jobs);
  return PIPELINE.map(stage => ({
    stage,
    jobs: active
      .filter(job => stage.statuses.includes(job.status))
      .sort((a, b) => {
        if (sortByPosition) return (a.position ?? 0) - (b.position ?? 0);
        return (a.lastStatusChange || 0) - (b.lastStatusChange || 0);
      }),
  }));
}

// ─── Blocker helpers ──────────────────────────────────────────────────────────

/** Returns the active BlockerRecord on this truck, or null if unblocked. */
export function getActiveBlocker(truck) {
  return truck?.currentBlocker ?? null;
}

/** True if this truck has an active blocker. */
export function isBlocked(truck) {
  return !!getActiveBlocker(truck);
}

/** Milliseconds since the active blocker was set (0 if not blocked). */
export function getBlockerAge(truck, now = Date.now()) {
  const blocker = getActiveBlocker(truck);
  if (!blocker?.setAt) return 0;
  return now - blocker.setAt * 1000;
}

// ─── Activity stream helpers ──────────────────────────────────────────────────

/**
 * Returns a normalized activities array for any truck regardless of age.
 * New trucks have activities[]; old trucks have notes[].
 * Legacy notes are wrapped as ACTIVITY_TYPES.NOTE entries so consumers
 * always see the same shape.
 */
// The mobile app writes the WHOLE notes array as one JSON string into a
// [String] field, so the record comes back as ["[{...},{...}]"] rather than
// ["{...}", "{...}"]. Un-nest that here, or the reader downstream shows the
// raw JSON as note text — which is why a photo taken on the phone rendered as
// its own metadata instead of an image.
function flattenLegacyNotes(notes) {
  const out = [];
  for (const entry of notes) {
    if (!entry) continue;
    const t = typeof entry === 'string' ? entry.trim() : entry;
    if (typeof t === 'string' && t.startsWith('[')) {
      try {
        const parsed = JSON.parse(t);
        if (Array.isArray(parsed)) {
          for (const item of parsed) {
            if (item) out.push(typeof item === 'string' ? item : JSON.stringify(item));
          }
          continue;
        }
      } catch { /* not JSON — fall through and keep the raw string */ }
    }
    out.push(typeof entry === 'string' ? entry : JSON.stringify(entry));
  }
  return out;
}

// A legacy note is either plain text or a JSON blob the phone wrote:
// { text, timestamp, author, photoUrl }. Recover the metadata when it's there —
// a note that lands in the stream under the wrong author, or all clumped at one
// timestamp, is barely better than one that never shows up.
function legacyNoteToActivity(raw, i, truck) {
  let text = raw;
  let timestamp = null;
  let author = null;
  let photos = [];

  if (typeof raw === 'string' && raw.trim().startsWith('{')) {
    try {
      const n = JSON.parse(raw);
      if (n && typeof n === 'object') {
        text      = typeof n.text === 'string' ? n.text : raw;
        timestamp = n.timestamp ?? null;
        author    = n.author ?? null;
        photos    = n.photoUrl ? [n.photoUrl] : [];
      }
    } catch { /* not JSON — keep the raw string as the text */ }
  }

  return {
    id: `legacy-note-${truck.id}-${i}`,
    type: ACTIVITY_TYPES.NOTE,
    text,
    author,
    timestamp: timestamp || truck.lastNote || truck.lastStatusChange || null,
    photos,
    customerFacing: false,
    category: null,
    stage: STATUS_TO_STAGE[truck.status] ?? null,
  };
}

/**
 * Both sources, merged — not one or the other.
 *
 * The web writes typed entries to `activities`; the phone still writes strings
 * to the legacy `notes` field. Returning `activities` whenever it was non-empty
 * meant a single web note made every note a tech had written from the truck
 * disappear from the stream. Nothing has ever copied `notes` into `activities`
 * (no migration touches it, and no writer fills both), so merging them cannot
 * double up an entry.
 *
 * Newest first, matching how the web prepends to `activities`.
 */
export function normalizeActivities(truck) {
  const typed  = Array.isArray(truck?.activities) ? truck.activities : [];
  const legacy = Array.isArray(truck?.notes)
    ? flattenLegacyNotes(truck.notes).map((raw, i) => legacyNoteToActivity(raw, i, truck))
    : [];

  if (!legacy.length) return typed;

  // Sorted whenever legacy notes are in play, so a phone-only job reads the
  // same direction as a mixed one. Notes with no timestamp of their own share
  // the job's, and a stable sort keeps them in the order they were written.
  return [...typed, ...legacy].sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
}

export function getJobsForTech(jobs, techNumber) {
  const normalized = normalizeTechNumber(techNumber);
  return getActiveJobs(jobs)
    .filter(job => {
      const nums = (job.techNumbers || []).map(normalizeTechNumber).filter(Boolean);
      return nums.includes(normalized);
    })
    .sort((a, b) => (a.lastStatusChange || 0) - (b.lastStatusChange || 0));
}

// ─── Road service status machine ─────────────────────────────────────────────
//
// Shop jobs move through the pipeline above. Road calls are a different animal:
// one tech, one truck, a drive out and a drive back, and a customer who is
// counting minutes. This machine is the single definition of that flow for both
// the native tech app and the web dispatch board.
//
// The status strings are the ones already in the database (OPEN, TRAVELING,
// ARRIVED…). The plain-English names the tech sees live in ROAD_STATUS_LABELS.

export const ROAD_STATUS = {
  INCOMING:  'OPEN',        // dispatched, not yet accepted by a tech
  ASSIGNED:  'ASSIGNED',    // tech accepted it, hasn't rolled
  EN_ROUTE:  'TRAVELING',
  ON_SCENE:  'ARRIVED',
  HOLD:      'HOLD',
  COMPLETE:  'COMPLETED',
  CANCELLED: 'CANCELLED',
};

export const ROAD_STATUS_LABELS = {
  OPEN:      'Incoming',
  ASSIGNED:  'Assigned',
  TRAVELING: 'En Route',
  ARRIVED:   'On Scene',
  HOLD:      'On Hold',
  COMPLETED: 'Complete',
  CANCELLED: 'Cancelled',
};

// Statuses written by the older road service screens. They still exist on live
// rows, so the machine reads them as their nearest equivalent instead of
// stranding a call with no valid buttons.
export const ROAD_STATUS_ALIASES = {
  DIAGNOSING:    'ARRIVED',
  REPAIRING:     'ARRIVED',
  WAITING_PARTS: 'HOLD',
};

export function canonicalRoadStatus(status) {
  const raw = String(status || '').toUpperCase();
  return ROAD_STATUS_ALIASES[raw] || raw;
}

// Forward path: ASSIGNED → TRAVELING → ARRIVED → COMPLETED.
// Hold hangs off the three working states and returns to whichever one it left,
// so a tech can stop the clock for parts or authorization and pick back up.
const ROAD_TRANSITIONS = {
  OPEN:      ['ASSIGNED', 'CANCELLED'],
  ASSIGNED:  ['TRAVELING', 'HOLD', 'CANCELLED'],
  TRAVELING: ['ARRIVED', 'HOLD', 'CANCELLED'],
  ARRIVED:   ['COMPLETED', 'HOLD', 'CANCELLED'],
  HOLD:      ['ASSIGNED', 'TRAVELING', 'ARRIVED', 'CANCELLED'],
  COMPLETED: [],
  CANCELLED: [],
};

export const ROAD_HOLD_REASONS = [
  { id: 'WAITING_PARTS',         label: 'Waiting on Parts' },
  { id: 'WAITING_AUTHORIZATION', label: 'Waiting on Authorization' },
  { id: 'CUSTOMER_CALLBACK',     label: 'Customer Callback' },
  { id: 'WAITING_TOW',           label: 'Waiting on Tow' },
  { id: 'OTHER',                 label: 'Other' },
];

export function roadHoldReasonLabel(id) {
  const match = ROAD_HOLD_REASONS.find(r => r.id === id);
  return match ? match.label : (id || 'On Hold');
}

export const ROAD_ACTIVE_STATUSES = ['OPEN', 'ASSIGNED', 'TRAVELING', 'ARRIVED', 'HOLD',
  ...Object.keys(ROAD_STATUS_ALIASES)];

export function isRoadCallActive(call) {
  const status = canonicalRoadStatus(call?.status);
  return status !== 'COMPLETED' && status !== 'CANCELLED' && !!status;
}

// Where a hold returns to. Prefer what was recorded when the hold was set;
// fall back to the last working state in the history, then to ARRIVED — a tech
// who held on scene is far more likely to resume there than to re-drive.
export function roadResumeStatus(call, events = []) {
  const stored = canonicalRoadStatus(call?.holdResumeStatus);
  if (stored && stored !== 'HOLD') return stored;

  const priorWorking = [...events]
    .sort((a, b) => (b.at || 0) - (a.at || 0))
    .map(e => canonicalRoadStatus(e.status))
    .find(s => s === 'TRAVELING' || s === 'ARRIVED' || s === 'ASSIGNED');

  return priorWorking || 'ARRIVED';
}

/**
 * The transitions a tech may take right now, in button order.
 * A held call offers exactly one way forward — back where it came from — so the
 * tech is never asked to re-pick a state they already passed through.
 */
export function availableRoadTransitions(call, events = []) {
  const status = canonicalRoadStatus(call?.status);
  const allowed = ROAD_TRANSITIONS[status] || [];

  if (status === 'HOLD') {
    const resume = roadResumeStatus(call, events);
    return allowed.filter(next => next === resume || next === 'CANCELLED');
  }
  return [...allowed];
}

export function canTransitionRoadCall(call, next, events = []) {
  return availableRoadTransitions(call, events).includes(canonicalRoadStatus(next));
}

// The flat timestamp columns the dispatch boards and portal-to-portal billing
// already read. Written alongside the event log, never replaced by it.
export function roadTimestampPatch(status, at) {
  switch (canonicalRoadStatus(status)) {
    case ROAD_STATUS.ASSIGNED:  return { acceptedAt: at };
    case ROAD_STATUS.EN_ROUTE:  return { travelStartedAt: at };
    case ROAD_STATUS.ON_SCENE:  return { arrivedAt: at, workStartedAt: at };
    case ROAD_STATUS.HOLD:      return { holdStartedAt: at };
    case ROAD_STATUS.COMPLETE:  return { completedAt: at };
    case ROAD_STATUS.CANCELLED: return { cancelledAt: at };
    default: return {};
  }
}

/**
 * The patch that moves a call to its next state. Lives here so the native app
 * and the dispatch board can't drift on what a transition means — a hold set on
 * the board has to look exactly like a hold set on a phone, or the report reads
 * two different stories.
 */
export function buildRoadCallPatch({
  call,
  next,
  at,
  technicianId = null,
  holdReason = null,
  holdNote = null,
}) {
  const fromStatus = canonicalRoadStatus(call?.status);
  const status = canonicalRoadStatus(next);
  const isHold = status === ROAD_STATUS.HOLD;
  const isAccept = status === ROAD_STATUS.ASSIGNED && fromStatus === ROAD_STATUS.INCOMING;

  const patch = { id: call.id, status, ...roadTimestampPatch(status, at) };

  if (isAccept) {
    patch.assignedTechnicianId = technicianId || call.assignedTechnicianId || null;
    patch.acceptedByTechnicianId = technicianId || null;
  }

  if (isHold) {
    patch.currentHoldReason = holdReason || 'OTHER';
    patch.currentHoldNote = holdNote || null;
    patch.holdResumeStatus = fromStatus;
  } else if (fromStatus === ROAD_STATUS.HOLD) {
    // Resuming clears the current hold. The event log keeps the record of it.
    patch.currentHoldReason = null;
    patch.currentHoldNote = null;
    patch.holdStartedAt = null;
    patch.holdResumeStatus = null;
  }

  return patch;
}

// Activity-stream wording for a transition. Shared so a hold logged from a
// phone and one logged from the board read identically in the history.
export function roadTransitionNote(next, holdReason = null, holdNote = null) {
  const status = canonicalRoadStatus(next);
  if (status === ROAD_STATUS.HOLD) {
    return `Road service on hold — ${roadHoldReasonLabel(holdReason || 'OTHER')}${holdNote ? `: ${holdNote}` : ''}`;
  }
  return `Road service ${(ROAD_STATUS_LABELS[status] || status).toLowerCase()}`;
}

/**
 * The history row for one transition. `id` is supplied by the caller so an
 * offline replay can write the same row twice without creating two.
 */
export function buildRoadStatusEvent({
  call,
  next,
  at,
  id,
  technicianId = null,
  techNumber = null,
  techName = null,
  holdReason = null,
  holdNote = null,
  source = null,
  events = [],
  defaultLocationId = null,
}) {
  const fromStatus = canonicalRoadStatus(call?.status);
  const status = canonicalRoadStatus(next);
  const isHold = status === ROAD_STATUS.HOLD;

  return {
    id,
    roadServiceCallId: call.id,
    locationId: call.locationId || defaultLocationId,
    status,
    fromStatus: fromStatus || null,
    at,
    technicianId: technicianId || call.assignedTechnicianId || null,
    techNumber: techNumber ? String(techNumber) : null,
    techName: techName || null,
    holdReason: isHold ? (holdReason || 'OTHER') : null,
    holdNote: isHold ? (holdNote || null) : null,
    resumeStatus: isHold ? (fromStatus || roadResumeStatus(call, events)) : null,
    source,
  };
}

/**
 * Response-time report for one call, from its transition history.
 * Every span is seconds; null means that leg never happened. onSceneSeconds
 * excludes hold time, because "how long was the tech working" and "how long was
 * the truck sitting" are different questions and customers ask both.
 *
 * Pass the call as well when you have it: rows dispatched before the event log
 * existed have no OPEN event, and call.dispatchedAt is the only honest start.
 */
export function summarizeRoadResponse(events = [], call = null) {
  const ordered = [...events]
    .filter(e => e && Number.isFinite(e.at))
    .sort((a, b) => a.at - b.at)
    .map(e => ({ ...e, status: canonicalRoadStatus(e.status) }));

  const firstAt = status => ordered.find(e => e.status === status)?.at ?? null;
  const lastAt = status => [...ordered].reverse().find(e => e.status === status)?.at ?? null;

  const dispatchedAt =
    firstAt('OPEN') ??
    (Number.isFinite(call?.dispatchedAt) ? call.dispatchedAt : null) ??
    ordered[0]?.at ?? null;
  const acceptedAt = firstAt('ASSIGNED');
  const enRouteAt = firstAt('TRAVELING');
  const onSceneAt = firstAt('ARRIVED');
  const completedAt = lastAt('COMPLETED');

  // Hold spans: each HOLD event runs until the next event of any kind.
  let holdSeconds = 0;
  ordered.forEach((event, i) => {
    if (event.status !== 'HOLD') return;
    const end = ordered[i + 1]?.at;
    if (end) holdSeconds += Math.max(0, end - event.at);
  });

  // Truthiness is the wrong test here — a timestamp is a number and 0 is a
  // legal one. Only "absent" should read as absent.
  const span = (from, to) =>
    (Number.isFinite(from) && Number.isFinite(to) && to >= from ? to - from : null);
  const onSceneSpan = span(onSceneAt, completedAt);

  return {
    dispatchedAt,
    acceptedAt,
    enRouteAt,
    onSceneAt,
    completedAt,
    acceptSeconds: span(dispatchedAt, acceptedAt),   // dispatch → tech accepts
    rollSeconds: span(acceptedAt, enRouteAt),        // accept → wheels moving
    driveSeconds: span(enRouteAt, onSceneAt),        // wheels moving → on scene
    onSceneSeconds: onSceneSpan === null ? null : Math.max(0, onSceneSpan - holdSeconds),
    holdSeconds,
    totalSeconds: span(dispatchedAt, completedAt),
  };
}